import {
  Icon,
  IconButton,
  IconButtonProps,
  useColorModeValue,
} from "@chakra-ui/react";
import React, { useCallback, useContext, useMemo } from "react";
import { AiFillStar, AiOutlineStar } from "react-icons/ai";
import { useLocation } from "react-router-dom";
import { FavouritesContext } from "../context/favourites.context";
import ReactGA from "react-ga4";

type Props = Partial<IconButtonProps> & {
  name: string;
};

const FavouriteButton = ({ name, ...props }: Props) => {
  const { pathname, search } = useLocation();
  const starUnselected = useColorModeValue("blackAlpha.500", "whiteAlpha.500");
  const favouriteUrl = useMemo(() => pathname + search, [pathname, search]);
  const { favourites, addFavourite, removeFavourite } =
    useContext(FavouritesContext);
  const isFavourite = useMemo(
    () => favourites.some((f) => f.url === favouriteUrl),
    [favourites, favouriteUrl]
  );

  const handleFavourite = useCallback(() => {
    if (!isFavourite) {
      ReactGA.event({
        category: "user_interaction",
        action: `added favourite`,
      });
      addFavourite({ name, url: favouriteUrl });
    } else {
      ReactGA.event({
        category: "user_interaction",
        action: `removed favourite`,
      });
      removeFavourite(favouriteUrl);
    }
  }, [isFavourite, favouriteUrl, name, addFavourite, removeFavourite]);

  return (
    <IconButton
      {...props}
      aria-label={isFavourite ? "Remove from favourites" : "Add to favourites"}
      title={isFavourite ? "Remove from favourites" : "Add to favourites"}
      onClick={handleFavourite}
      role="button"
      isLoading={isFavourite === undefined}
      variant="ghost"
      size="sm"
      icon={
        isFavourite ? (
          <Icon as={AiFillStar} w={25} h={25} color={"orange.300"} />
        ) : (
          <Icon as={AiOutlineStar} w={25} h={25} color={starUnselected} />
        )
      }
    />
  );
};

export default FavouriteButton;
